import { useProcurement } from "@/context/ProcurementContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/utils";
import { 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  FileText,
  Package,
  Users,
  ArrowRight,
  Bell
} from "lucide-react";

interface PendingActionsProps {
  onNavigate?: (path: string) => void;
}

export const PendingActions = ({ onNavigate }: PendingActionsProps) => {
  const { state } = useProcurement();

  // Calculate pending actions
  const pendingActions = [
    // Pending PO approvals
    ...state.purchaseOrders
      .filter(po => po.status === 'CREATED')
      .map(po => ({
        id: `po-${po.id}`,
        type: 'approval',
        title: `Approve PO ${po.poNumber}`,
        description: `Purchase order worth ${formatCurrency(po.totalAmount)}`,
        priority: po.priority,
        createdAt: po.createdAt,
        icon: FileText,
        color: 'blue'
      })),
    
    // Low stock alerts
    ...state.skus
      .filter(sku => sku.currentStock <= sku.reorderLevel)
      .slice(0, 3) // Limit to top 3 most critical
      .map(sku => ({
        id: `stock-${sku.id}`,
        type: 'stock',
        title: `Low Stock: ${sku.name}`,
        description: `Only ${sku.currentStock} ${sku.unit} remaining`,
        priority: sku.currentStock <= 0 ? 'Urgent' : 'High',
        createdAt: sku.updatedAt,
        icon: Package,
        color: 'orange'
      })),
    
    // Pending quality inspections
    ...state.qualityInspections
      .filter(qi => qi.overallStatus === 'Pending' || qi.overallStatus === 'In Progress')
      .map(qi => ({
        id: `inspection-${qi.id}`,
        type: 'inspection',
        title: `Quality Inspection Required`,
        description: `PO ${state.purchaseOrders.find(po => po.id === qi.poId)?.poNumber || qi.poId}`,
        priority: 'Medium',
        createdAt: qi.inspectionDate,
        icon: CheckCircle,
        color: 'green'
      })),
    
    // Overdue invoices
    ...state.invoices
      .filter(inv => {
        const dueDate = new Date(inv.dueDate);
        const today = new Date();
        return inv.status !== 'Paid' && dueDate < today;
      })
      .map(inv => ({
        id: `invoice-${inv.id}`,
        type: 'payment',
        title: `Overdue Payment`,
        description: `Invoice ${inv.invoiceNumber} - ${formatCurrency(inv.totalAmount)}`,
        priority: 'Urgent',
        createdAt: inv.dueDate,
        icon: AlertTriangle,
        color: 'red'
      }))
  ];

  // Sort by priority and date
  const sortedActions = pendingActions
    .sort((a, b) => {
      const priorityOrder = { 'Urgent': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
      const aPriority = priorityOrder[a.priority as keyof typeof priorityOrder] ?? 4;
      const bPriority = priorityOrder[b.priority as keyof typeof priorityOrder] ?? 4;
      
      if (aPriority !== bPriority) {
        return aPriority - bPriority;
      }
      
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    })
    .slice(0, 8); // Show top 8 most important actions

  const getPriorityVariant = (priority: string) => {
    switch (priority) {
      case 'Urgent':
        return 'destructive';
      case 'High':
        return 'secondary';
      case 'Medium':
        return 'outline';
      case 'Low':
        return 'outline';
      default:
        return 'outline';
    }
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'blue':
        return 'text-blue-500 bg-blue-50 dark:bg-blue-950';
      case 'orange':
        return 'text-orange-500 bg-orange-50 dark:bg-orange-950';
      case 'green':
        return 'text-green-500 bg-green-50 dark:bg-green-950';
      case 'red':
        return 'text-red-500 bg-red-50 dark:bg-red-950';
      default:
        return 'text-gray-500 bg-gray-50 dark:bg-gray-950';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Pending Actions
            </CardTitle>
            <CardDescription>
              Items requiring your attention
            </CardDescription>
          </div>
          <Badge variant="secondary" className="text-xs">
            {pendingActions.length} total
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {sortedActions.length > 0 ? (
          <div className="space-y-3">
            {sortedActions.map((action) => {
              const Icon = action.icon;
              return (
                <div key={action.id} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getColorClasses(action.color)}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-sm truncate">{action.title}</h4>
                      <Badge variant={getPriorityVariant(action.priority)} className="text-xs">
                        {action.priority}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{action.description}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(action.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      if (action.type === 'approval') {
                        onNavigate?.('/purchase-orders');
                      } else if (action.type === 'stock') {
                        onNavigate?.('/skus');
                      } else if (action.type === 'inspection') {
                        onNavigate?.('/quality');
                      } else if (action.type === 'payment') {
                        onNavigate?.('/invoices');
                      }
                    }}
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>All caught up!</p>
            <p className="text-sm">No pending actions at this time</p>
          </div>
        )}
        
        {pendingActions.length > sortedActions.length && (
          <div className="mt-4 pt-3 border-t">
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => onNavigate?.('/purchase-orders')}
            >
              View All {pendingActions.length} Actions
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};